import React, { useMemo } from 'react';
import { View } from 'react-native';
import { styled } from 'dripsy';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { ValidationError } from 'yup';

import { Icon, BaseText, Separator } from '@components';
import { SignUpSchemaDataType, signUpSchema } from '@validation';

const RowContainer = styled(View)({
  flexDirection: 'row',
  alignItems: 'flex-start',
});

const TitleText = styled(BaseText)({
  color: '$heading800',
  variant: 'text.xsmall',
  fontWeight: 'semi',
});

const ValidationText = styled(BaseText)({
  color: '$heading800',
  variant: 'text.xsmall',
  paddingLeft: '$2',
});

const HintContainer = styled(View)({
  flexDirection: 'row',
  alignItems: 'center',
  backgroundColor: '$background10',
  borderRadius: 4,
  px: '$4',
  py: '$2',
});

export function PasswordValidation() {
  const { t } = useTranslation();
  const formik = useFormikContext<SignUpSchemaDataType>();

  function getErrorTypes() {
    try {
      signUpSchema.fields.password.validateSync(
        formik.values.password,
        {
          abortEarly: false,
        },
      );

      return [];
    } catch (error) {
      if (error instanceof ValidationError) {
        return error.inner.map((e) => e.type);
      }

      return [];
    }
  }

  const validation = useMemo(() => {
    const errorTypes = getErrorTypes();

    // TODO: may should we add exported const for test names?
    const isValidLength =
      !errorTypes.includes('min') && !errorTypes.includes('max');
    const isValidMustHaveLetter =
      !errorTypes.includes('mustHaveLetter');
    const isValidMustHaveNumber =
      !errorTypes.includes('mustHaveNumber');
    const isValidMustHaveNoSpace = !errorTypes.includes(
      'mustNotHaveSpace',
    );

    return {
      length: isValidLength,
      haveLetter: isValidMustHaveLetter,
      haveNumber: isValidMustHaveNumber,
      haveNoSpace: isValidMustHaveNoSpace,
    };
  }, [formik.values.password]);

  const lengthColor = validation.length
    ? '$primary300'
    : '$background50';

  const haveLetterColor = validation.haveLetter
    ? '$primary300'
    : '$background50';

  const haveNumberColor = validation.haveNumber
    ? '$primary300'
    : '$background50';

  const haveNoSpaceColor =
    validation.haveNoSpace && formik.values.password.length
      ? '$primary300'
      : '$background50';

  return (
    <View>
      <TitleText>{t('signUp.passwordHintTitle')}</TitleText>
      <Separator size="$4" />
      <RowContainer>
        <HintContainer>
          <Icon
            name="tick-square-bold"
            size={16}
            color={lengthColor}
          />
          <ValidationText>
            {/* // TODO: may should we add exported const for min/max values?
             */}
            {t('signUp.passwordHintLength', { min: 8, max: 64 })}
          </ValidationText>
        </HintContainer>
        <Separator size="$4" vertical />
        <HintContainer>
          <Icon
            name="tick-square-bold"
            size={16}
            color={haveLetterColor}
          />
          <ValidationText>
            {t('signUp.passwordHintLetter')}
          </ValidationText>
        </HintContainer>
      </RowContainer>
      <Separator size="$4" />
      <RowContainer>
        <HintContainer>
          <Icon
            name="tick-square-bold"
            size={16}
            color={haveNumberColor}
          />
          <ValidationText>
            {t('signUp.passwordHintNumber')}
          </ValidationText>
        </HintContainer>
        <Separator size="$4" vertical />
        <HintContainer>
          <Icon
            name="tick-square-bold"
            size={16}
            color={haveNoSpaceColor}
          />
          <ValidationText>
            {t('signUp.passwordHintNoSpace')}
          </ValidationText>
        </HintContainer>
      </RowContainer>
    </View>
  );
}
